import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Buffer } from 'buffer';
import { StringUtils } from 'maestro-ng';
import { appConstants } from '../app.constants';
import { UsuarioSessao } from '../models/usuario-sessao';
import { UsuarioService } from '../usuario/usuario.service';
import { KeycloakService } from 'keycloak-angular';

@Injectable()
export class AutenticacaoService {
  private static PATHS_PUBLICOS = ['/dashboard', '/semPermissao', '/autenticacao/logar'];

  private autenticacaoEmitter = new EventEmitter<UsuarioSessao>();
  private recursos: string[];

  constructor(private router: Router, private usuarioService: UsuarioService, private keycloak: KeycloakService) {
    const recursos = JSON.parse(localStorage.getItem(appConstants.chaveRecursos));

    if (recursos !== null && recursos !== undefined) {
      this.recursos = recursos;
    }
  }

  public getUsuarioSessao(): UsuarioSessao {
    const us = JSON.parse(localStorage.getItem(appConstants.chaveUsuarioSessao));

    let usuarioSessao: UsuarioSessao;

    if (us !== undefined && us !== null) {
      usuarioSessao = new UsuarioSessao(us.id, us.nome, us.login, us.permissoes, us.token, new Date(us.tokenExpiracao));
    }

    return usuarioSessao;
  }

  private parseToken(token: string): any {
    return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
  }

  private converterTokenParaUsuarioSessao(token: string): UsuarioSessao {
    const jwt = this.parseToken(token);

    return new UsuarioSessao(jwt.id, jwt.sub, jwt.login, jwt.permissoes, token, new Date(jwt.exp * 1000));
  }

  private definirUsuarioSessao(usuarioSessao: UsuarioSessao): void {
    localStorage.setItem(appConstants.chaveUsuarioSessao, JSON.stringify(usuarioSessao));
  }

  private definirRecursos(token: string): void {
    const jwt = this.parseToken(token);

    this.recursos = jwt.recursos;

    localStorage.setItem(appConstants.chaveRecursos, JSON.stringify(this.recursos));
  }

  public isAutenticado(): boolean {
    const usuarioSessao = this.getUsuarioSessao();

    return usuarioSessao !== undefined && usuarioSessao.tokenExpiracao > new Date();
  }

  public getRecursos(): string[] {
    return this.recursos;
  }

  public isDefinidoRecursos(): boolean {
    return this.getRecursos() !== undefined;
  }

  public async deslogar(redirecionar = true): Promise<void> {
    localStorage.removeItem(appConstants.chaveRedirecionarPara);
    localStorage.removeItem(appConstants.chaveUsuarioSessao);
    localStorage.removeItem(appConstants.chaveDataUltimoRefreshToken);
    localStorage.removeItem(appConstants.chaveRecursos);
    localStorage.removeItem('urlNova');

    this.recursos = undefined;

    this.autenticacaoEmitter.emit();

    if (redirecionar) {
      this.router.navigate(['/']);
    }

    await this.keycloak.logout(window.location.href);
  }

  public getAutenticacaoObservable(): EventEmitter<UsuarioSessao> {
    return this.autenticacaoEmitter;
  }

  private getRecursosDeFrontend(): string[] {
    return this.getRecursosPorPrefixo('front:');
  }

  private getRecursosDeBackend(): string[] {
    return this.getRecursosPorPrefixo('back:');
  }

  private getRecursosPorPrefixo(prefixo: string): string[] {
    return this.isDefinidoRecursos()
      ? this.getRecursos()
        .filter((p) => p.startsWith(prefixo))
        .map((p) => p.substr(prefixo.length))
      : [];
  }

  public temPermissaoAcesso(recurso: string): boolean {
    return this.temPermissao(recurso, this.getRecursosDeFrontend());
  }

  public temPermissaoExecucao(recurso: string): boolean {
    const recursos = this.getRecursosDeFrontend().concat(this.getRecursosDeBackend());

    return this.temPermissao(recurso, recursos);
  }

  private temPermissao(recurso: string, recursos: string[]): boolean {
    const recursoItens = this.recuperarPathItens(recurso);
    let temPermissao = false;

    for (let i = 0; i < recursos.length; i++) {

      const recursoCorrente = recursos[i];

      const recursoCorrenteItens = this.recuperarPathItens(recursoCorrente);

      if (recursoCorrenteItens.length <= recursoItens.length) {
        let corresponde = true;

        for (let j = 0; j < recursoCorrenteItens.length; j++) {
          const recursoCorrenteItem = recursoCorrenteItens[j];
          const recursoItem = recursoItens[j];

          if (recursoCorrenteItem !== recursoItem && recursoCorrenteItem !== '*') {
            corresponde = false;
          }
        }

        if (corresponde) {
          temPermissao = true;
          break;
        }
      }
    }

    return temPermissao;
  }

  private recuperarPathItens(path: string): string[] {
    return path.split('/').slice(1);
  }

  public verificaPermissao(routerEvent: any): void {
    let path = routerEvent.url;

    const indexPontoVirgula = path.indexOf(';');
    if (indexPontoVirgula !== -1) {
      path = path.substring(0, indexPontoVirgula);
    }

    if (!this.isPathPublico(path)) {
      const temAutenticacao = this.isAutenticado();
      let temPermissao = false;

      if (temAutenticacao) {
        temPermissao = this.temPermissaoAcesso(path);
      }

      if (!temAutenticacao) {
        localStorage.setItem(appConstants.chaveRedirecionarPara, path);
        this.router.navigate(['/']);
      } else if (!temPermissao) {
        this.router.navigate(['/semPermissao'], { queryParams: { recurso: path } });
      }
    }
  }

  public isPathPublico(path: string): boolean {
    let resultado = false;

    if (path === '/') {
      resultado = true;
    } else {
      for (let i = 0; i < AutenticacaoService.PATHS_PUBLICOS.length; i++) {
        if (path.indexOf(AutenticacaoService.PATHS_PUBLICOS[i]) === 0) {
          resultado = true;
          break;
        }
      }
    }
    return resultado;
  }

  public processarTokenAtualizado(token: string): UsuarioSessao {
    const usuarioSessao = this.converterTokenParaUsuarioSessao(token);

    this.definirUsuarioSessao(usuarioSessao);

    return usuarioSessao;
  }

  public inicializarSessao(token: string): void {
    const usuarioSessao = this.processarTokenAtualizado(token);
    this.definirRecursos(token);
    this.autenticacaoEmitter.emit(usuarioSessao);

    let irPara = localStorage.getItem(appConstants.chaveRedirecionarPara);

    if (!StringUtils.isUndefinedOrEmpty(irPara)) {
      this.router.navigateByUrl(decodeURIComponent(irPara));
    } else {
      this.router.navigate(['/']);
    }
  }
}
